import React, { useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import algolia from '../utilities/algolia';
import { StackedDialog } from '../utilities/ModalStackContext';

// Searchable car picker (Algolia cars index) used to link or swap the car on a
// credit app. Calls onSelect(car) with the chosen Algolia hit.
export default function CreditAppCarPickerDialog({ open, onClose, onSelect, title = 'Link Car' }) {
  const [query, setQuery] = useState('');
  const [cars, setCars] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open) { setQuery(''); setCars([]); setSelected(null); setLoading(false); }
  }, [open]);

  const handleSearch = async (q) => {
    setQuery(q);
    if (!q.trim()) { setCars([]); return; }
    setLoading(true);
    try {
      const index = algolia.client.initIndex('cars');
      const { hits } = await index.search(q);
      const sorted = hits.sort((a, b) => {
        const sa = a.stock ? parseInt(a.stock.replace(/\D/g, ''), 10) : 0;
        const sb = b.stock ? parseInt(b.stock.replace(/\D/g, ''), 10) : 0;
        return sb - sa;
      });
      setCars(sorted);
    } catch (e) {
      setCars([]);
    } finally {
      setLoading(false);
    }
  };

  const label = (car) =>
    `${car.year || ''} ${car.make || ''} ${car.model || ''}`.trim();

  const confirm = () => {
    if (selected) onSelect(selected);
  };

  return (
    <StackedDialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: 480 } }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Search cars"
          placeholder="Stock number, make, model…"
          value={query}
          onChange={e => handleSearch(e.target.value)}
          autoComplete="off"
          margin="normal"
          size="small"
        />
        {loading && (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {cars.map(car => (
            <Grid item xs={12} sm={6} md={4} key={car.objectID}>
              <Card
                onClick={() => setSelected(car)}
                sx={{
                  cursor: 'pointer',
                  border: selected?.objectID === car.objectID ? '2px solid #1976d2' : '1px solid #eee',
                  '&:hover': { boxShadow: 3 },
                }}
              >
                <CardMedia component="img" height="120" image={car.thumbnail || '/missing_image.jpeg'} alt={label(car)} sx={{ objectFit: 'cover' }} />
                <CardContent sx={{ py: 1 }}>
                  <Typography variant="subtitle2" noWrap>{car.stock || 'No stock'}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>{label(car)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={confirm} disabled={!selected}>Link this car</Button>
      </DialogActions>
    </StackedDialog>
  );
}
